import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plane } from 'lucide-react';
import type {
  ColumbusPlannerFlightRouteModel,
  ColumbusPlannerFlightRouteStopModel,
} from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';

interface Props {
  route: ColumbusPlannerFlightRouteModel | null;
  isLoading: boolean;
  /** Když true, zobrazí se jen první přelet, který přelétá hranice (změna IsDomestic). */
  compact?: boolean;
}

const timeFormatter = new Intl.DateTimeFormat('cs-CZ', {
  hour: '2-digit',
  minute: '2-digit',
});

function formatTime(value: string | null | undefined): string {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return timeFormatter.format(d);
}

// V kompaktním režimu chceme zobrazit jen první "přelet hranice" — tedy první dvojici
// po sobě jdoucích zastávek, kde se mění IsDomestic. Když žádný takový přechod
// neexistuje, zobrazíme aspoň první a poslední zastávku jako shrnutí trasy.
function getCompactStops(
  stops: readonly ColumbusPlannerFlightRouteStopModel[]
): readonly ColumbusPlannerFlightRouteStopModel[] {
  if (stops.length <= 2) return stops;
  for (let i = 0; i < stops.length - 1; i++) {
    if (stops[i]!.isDomestic !== stops[i + 1]!.isDomestic) {
      return [stops[i]!, stops[i + 1]!];
    }
  }
  return [stops[0]!, stops[stops.length - 1]!];
}

export function FlightRouteVisualization({ route, isLoading, compact = false }: Props) {
  const { t } = useTranslation('columbus-planner');

  const displayStops = useMemo(
    () => (route ? (compact ? getCompactStops(route.stops) : route.stops) : []),
    [route, compact]
  );

  return (
    <div>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="text-phx-text-faint h-5 w-5 animate-spin" />
        </div>
      ) : !route ? (
        <p className="text-phx-text-muted py-2 text-xs italic">
          {t('buyTickets.flightRoute.notFound')}
        </p>
      ) : (
        <div className="flex w-full items-stretch justify-center gap-1 overflow-x-auto py-1">
          {displayStops.map((stop, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === displayStops.length - 1;
            return (
              <div key={stop.order} className="flex items-stretch">
                <div className="flex min-w-14 flex-col items-center text-center">
                  <span className="text-phx-text text-sm font-bold">{stop.stationCode}</span>
                  <span className="text-phx-text-2 text-phx-2xs mt-0.5 font-mono leading-tight">
                    {isFirst ? ' ' : formatTime(stop.arrival)}
                  </span>
                  <span className="text-phx-text-2 text-phx-2xs font-mono leading-tight">
                    {isLast ? ' ' : formatTime(stop.departure)}
                  </span>
                </div>
                {!isLast && (
                  <div className="flex flex-col items-center justify-start pt-2">
                    <div
                      className={cn(
                        'relative flex items-center justify-center',
                        compact ? 'w-12 md:w-16' : 'w-12 md:w-16 lg:w-24',
                        'before:bg-border before:absolute before:top-1/2 before:left-0 before:h-px before:w-full before:-translate-y-1/2'
                      )}
                    >
                      <span className="bg-card relative inline-flex h-6 w-6 items-center justify-center rounded-full">
                        <Plane
                          className="text-phx-text-muted h-3 w-3 rotate-45"
                          aria-hidden="true"
                        />
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
